import { AppProviderContext } from '@websrc/App/AppContext'
import { getAnchorPropsFromLink } from '@websrc/App/Helpers/getAnchorPropsFromLink'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { CombinationOfMtmParams, MtmParams, useMTMParams } from '@websrc/App/Hooks/useMTMParams'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapLink } from '@websrc/DataMappers/mapLink'
import React, { ComponentPropsWithoutRef, useContext } from 'react'

import * as SC from './SpotLink.style'

export interface SpotLinkAnchorProps extends ComponentPropsWithoutRef<'a'> {
  link?: ReturnType<typeof mapLink>
  mtm_params?: MtmParams
  combination_of_mtm_params?: CombinationOfMtmParams
  ctaType?: SC.SpotLinkTypes
  ctaVariant?: SC.SpotLinkVariants
  noStyles?: boolean
  allowEmptyLink?: boolean
  linkSection?: (string | undefined | null)[]
  linkSubSection?: (string | undefined | null)[]
}

export function SpotLinkAnchor({
  link,
  ctaType = 'secondary',
  ctaVariant = 'blue',
  mtm_params,
  combination_of_mtm_params,
  allowEmptyLink,
  linkSection,
  linkSubSection = [],
  onClick,
  ...passedAnchorProps
}: SpotLinkAnchorProps) {
  const locale = useLocale()
  const { matomoPageTemplate } = useContext(AppProviderContext)
  const resultMTMs = useMTMParams(combination_of_mtm_params, mtm_params)

  if (!link) return null

  const anchorProps = getAnchorPropsFromLink(link, locale)

  const isEmpty = !Object.keys(anchorProps).length

  if (isEmpty && !allowEmptyLink) {
    return null
  }

  const { internal_link } = link

  const linkOnClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (linkSection?.length) {
      matomo.trackEvent(
        'engagement',
        matomoPageTemplate,
        [...linkSection, 'spot: link-anchor'],
        linkSubSection.concat([link.cta_text]),
        'button'
      )
    }

    onClick?.(event)
  }
  const { href, ...restAnchorProps } = anchorProps
  // Internal link -> use Gatsby Link unless it opens new tab
  if (internal_link?.length && !link.open_in_new_tab) {
    return (
      <SC.StyledLink
        ctaType={ctaType}
        ctaVariant={ctaVariant}
        isEmpty={isEmpty}
        {...restAnchorProps}
        {...passedAnchorProps}
        onClick={linkOnClick}
        to={href + resultMTMs ?? '#'}
      />
    )
  }

  return (
    <SC.StyledAnchor
      ctaType={ctaType}
      ctaVariant={'blue'}
      isEmpty={isEmpty}
      {...restAnchorProps}
      {...passedAnchorProps}
      href={href + resultMTMs}
      onClick={linkOnClick}
    />
  )
}
